import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";

import HtmlText from "../common/HtmlText";
import CalendlyConversionTracker from "@/components/analytics/CalendlyConversionTracker";

// ----------------------------------------------------------------------------------------

// Display-only Calendly embed params — hide the event-type sidebar + the
// widget's own GDPR banner (the site has its own cookie consent). Applied at
// render time so the Strapi `form` field stores just the clean base scheduling
// URL. Stripping any query the field already carries first makes this
// idempotent: works whether Strapi stores the base or a full URL, and never
// double-appends.
const CALENDLY_EMBED_PARAMS = "hide_event_type_details=1&hide_gdpr_banner=1";

const buildCalendlyEmbedUrl = (raw: string): string => {
  if (!raw) return raw;
  const base = raw.split("?")[0].split("#")[0];
  return `${base}?${CALENDLY_EMBED_PARAMS}`;
};

export type IScheduleMeetingSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  description?: ReactNode;
  images?: { src: string; alt: string }[];
  form: string;
};

const ScheduleMeetingSection: FC<IScheduleMeetingSectionProps> = (props) => {
  const { heading, description, images, form, className, ...other } = props;

  const embedUrl = buildCalendlyEmbedUrl(form);

  // Only render images that actually have a source. Strapi's repeatable image
  // component can hold entries whose media relation is null (a slot added in
  // the admin but never given a file); getImageUrl() returns "" for those, and
  // <Image src=""> renders a broken-image icon. The /schedule block currently
  // has 9 such empty slots — filtering here means null/empty entries never
  // paint a broken icon, regardless of the CMS content state.
  const validImages = (images ?? []).filter((img) => Boolean(img?.src));

  return (
    <section
      className={clsx("py-5 sm:container lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      <div className="lg:grid lg:grid-cols-2">
        {(heading || description || images) && (
          <div className="px-6 sm:px-0 lg:pr-8 xl:pr-12">
            {heading && (
              <h1 className="mb-3 w-full text-h5 font-medium text-black-900 lg:mb-5 lg:text-h3 xl:mb-7 xl:text-h2 2xl:w-10/12">
                {heading}
              </h1>
            )}

            {description && <HtmlText>{description}</HtmlText>}

            {validImages.length > 0 && (
              <div className="mb-8 mt-[30px] grid grid-cols-2 items-center gap-x-4 gap-y-4 md:grid-cols-5 lg:mb-0 lg:mt-[50px] lg:grid-cols-4 xl:mt-[90px]">
                {validImages.map(({ src, alt }, idx) => (
                  <Image
                    key={idx}
                    className="mx-auto md:w-[80px] lg:w-[100px] 2xl:w-auto"
                    src={src}
                    alt={alt}
                    width={135}
                    height={84}
                    style={{
                      objectFit: "contain",
                      width: "auto",
                      height: "auto",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Calendly - Schedule meeting form */}
        <div className="w-full sm:bg-secondary-50 sm:p-7 sm:pt-6 xl:p-10 xl:pt-8">
          <div className="mb-3 pl-6 text-h7 font-normal sm:pl-0 sm:text-h5 lg:mb-5 lg:text-3xl xl:mb-[23px]">
            Book a Discovery Call
          </div>
          {/* Listens for Calendly's postMessage `event_scheduled` and fires the
              booking conversion. Without this, completed bookings inside the
              cross-origin iframe are invisible to analytics. */}
          <CalendlyConversionTracker />
          <iframe
            className="h-[600px] w-full bg-white"
            src={embedUrl}
            title="Book a discovery call with Certbar Security"
            // The widget sits below the fold on most viewports; deferring it
            // keeps it off the critical path so the page paints without
            // waiting on Calendly's bundle.
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      </div>
    </section>
  );
};

export default ScheduleMeetingSection;
